/**
 * 交易所数据采集配置页面
 * 路径：/system/exchange-data-settings
 *
 * 功能说明：
 * 1. 管理员配置支持的交易所品种及采集参数
 * 2. 支持配置数据源（实盘/模拟）和时间帧（1m/5m/15m/1h/4h/1d）
 * 3. 支持采集频率配置
 * 4. 支持启用/禁用控制
 *
 * @author page
 * @date 2026-04-24
 */

import React, {useEffect, useState} from 'react';
import {
    App,
    Button,
    Checkbox,
    Form,
    Input,
    InputNumber,
    Modal,
    Select,
    Space,
    Switch,
    Table,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import {DeleteOutlined, EditOutlined, PlusOutlined, ReloadOutlined, SettingOutlined,} from '@ant-design/icons';
import {exchangeDataConfigService} from '../../services/exchangeDataConfigService';
import type {
    ConfigType,
    DataSource,
    ExchangeDataConfig,
    ExchangeDataConfigRequest,
    ExchangeName,
    ProductType,
    Timeframe,
} from '../../types/exchangeDataConfig';
import {
    CONFIG_TYPE_OPTIONS,
    DATA_SOURCE_OPTIONS,
    EXCHANGE_OPTIONS,
    PRODUCT_TYPE_OPTIONS,
    TIMEFRAME_OPTIONS,
} from '../../constants/exchangeDataConfig';

const {Text} = Typography;

/** 数据源映射 */
const DATA_SOURCE_MAP: Record<DataSource, string> = {
    live: '实盘',
    paper: '模拟',
};

/** 时间帧映射 */
const TIMEFRAME_MAP: Record<Timeframe, string> = {
    '1m': '1分钟',
    '5m': '5分钟',
    '15m': '15分钟',
    '1h': '1小时',
    '4h': '4小时',
    '1d': '1天',
};

/** 产品类型映射 */
const PRODUCT_TYPE_MAP: Record<ProductType, string> = {
    contract: '合约',
    spot: '现货',
};

/** 配置类型映射 */
const CONFIG_TYPE_MAP: Record<ConfigType, string> = {
    setting: '用户配置',
    temporary: '系统归集',
};

/**
 * 交易所数据采集配置页面组件
 */
const ExchangeDataSettings: React.FC = () => {
    const {message: messageApi} = App.useApp();
    // ==================== 状态管理 ====================

    /** 数据加载状态 */
    const [loading, setLoading] = useState(false);

    /** 采集配置列表 */
    const [configs, setConfigs] = useState<ExchangeDataConfig[]>([]);

    /** 分页状态 */
    const [total, setTotal] = useState(0);
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);

    /** 筛选条件 */
    const [filterExchange, setFilterExchange] = useState<ExchangeName | undefined>(undefined);
    const [filterProductType, setFilterProductType] = useState<ProductType | undefined>(undefined);
    const [filterEnabled, setFilterEnabled] = useState<number | undefined>(undefined);
    const [filterConfigType, setFilterConfigType] = useState<ConfigType | undefined>(undefined);
    const [filterSymbol, setFilterSymbol] = useState<string | undefined>(undefined);

    /** 新增/编辑弹窗显示状态 */
    const [modalVisible, setModalVisible] = useState(false);
    /** 是否为编辑模式 */
    const [isEditMode, setIsEditMode] = useState(false);
    /** temporary top20 归集同步中 */
    const [syncingTemporary, setSyncingTemporary] = useState(false);
    /** 新增/编辑表单实例 */
    const [form] = Form.useForm();
    /** 当前编辑的记录ID */
    const [editingId, setEditingId] = useState<number | undefined>(undefined);

    // ==================== 数据加载 ====================

    /**
     * 筛选条件变化时重置到第一页
     */
    const handleFilterChange = <T, >(setter: React.Dispatch<React.SetStateAction<T | undefined>>) => (value: T) => {
        setter(value);
        setCurrentPage(0);
    };

    /**
     * 从后端分页加载采集配置
     */
    const loadConfigs = async () => {
        try {
            setLoading(true);
            const response = await exchangeDataConfigService.getConfigsPage({
                exchangeName: filterExchange,
                productType: filterProductType,
                configType: filterConfigType,
                enabled: filterEnabled,
                symbol: filterSymbol,
                page: currentPage,
                size: pageSize,
            });
            const apiResponse = response.data;
            if ('0' === apiResponse.code) {
                const pageData = apiResponse.data;
                setConfigs(pageData.content || []);
                setTotal(pageData.totalElements);
            } else {
                messageApi.error(apiResponse.message || '加载配置失败');
            }
        } catch (error: any) {
            console.error('加载配置失败:', error);
            messageApi.error('加载配置失败: ' + (error.message || '未知错误'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadConfigs();
    }, [filterExchange, filterProductType, filterConfigType, filterEnabled, filterSymbol, currentPage, pageSize]);

    // ==================== 配置操作 ====================

    /**
     * 打开新增配置弹窗
     */
    const handleAdd = () => {
        setIsEditMode(false);
        setEditingId(undefined);
        form.resetFields();
        form.setFieldsValue({
            configType: 'setting',
            enabled: true,
            sortOrder: 0,
        });
        setModalVisible(true);
    };

    /**
     * 打开编辑配置弹窗
     * @param record 配置记录
     */
    const handleEdit = (record: ExchangeDataConfig) => {
        setIsEditMode(true);
        setEditingId(record.id);
        form.setFieldsValue({
            configType: record.configType,
            exchangeName: record.exchangeName,
            symbol: record.symbol,
            coinSymbol: record.coinSymbol,
            productType: record.productType,
            dataSource: record.dataSource,
            timeframes: record.timeframes,
            fetchIntervalSec: record.fetchIntervalSec,
            sortOrder: record.sortOrder,
            enabled: 0 === record.enabled,
        });
        setModalVisible(true);
    };

    /**
     * 确认保存配置
     */
    const handleConfirm = async () => {
        try {
            const values = await form.validateFields();

            // 构建请求数据
            const req: ExchangeDataConfigRequest = {
                configType: values.configType || 'setting',
                exchangeName: values.exchangeName,
                symbol: values.symbol,
                coinSymbol: values.coinSymbol,
                productType: values.productType,
                dataSource: values.dataSource,
                timeframes: values.timeframes,
                fetchIntervalSec: values.fetchIntervalSec,
                sortOrder: values.sortOrder || 0,
                enabled: values.enabled ? 0 : 1,
            };

            if (isEditMode && editingId) {
                // 编辑模式
                await exchangeDataConfigService.updateConfig(editingId, req);
                messageApi.success('更新配置成功');
            } else {
                // 新增模式
                await exchangeDataConfigService.addConfig(req);
                messageApi.success('新增配置成功');
            }

            setModalVisible(false);
            await loadConfigs();
        } catch (error: any) {
            console.error('保存配置失败:', error);
            messageApi.error('保存配置失败: ' + (error.message || '未知错误'));
        }
    };

    /**
     * 删除配置
     * @param record 配置记录
     */
    const handleDelete = (record: ExchangeDataConfig) => {
        if ('temporary' === record.configType) {
            messageApi.warning('系统归集配置不可删除');
            return;
        }
        Modal.confirm({
            title: '确认删除',
            content: `确定要删除配置 ${record.exchangeName} - ${record.symbol} 吗？`,
            okText: '确定',
            cancelText: '取消',
            okButtonProps: {danger: true},
            className: 'dark-modal-confirm',
            onOk: async () => {
                try {
                    await exchangeDataConfigService.deleteConfig(record.id);
                    messageApi.success('删除配置成功');
                    await loadConfigs();
                } catch (error: any) {
                    console.error('删除配置失败:', error);
                    messageApi.error('删除配置失败: ' + (error.message || '未知错误'));
                }
            },
        });
    };

    /**
     * 切换配置启用/禁用状态
     * @param record 配置记录
     * @param checked 是否启用
     */
    const handleToggleEnabled = async (record: ExchangeDataConfig, checked: boolean) => {
        if ('temporary' === record.configType) {
            messageApi.warning('系统归集配置不可修改状态');
            return;
        }
        try {
            const enabled = checked ? 0 : 1;
            await exchangeDataConfigService.updateConfigStatus(record.id, enabled);
            messageApi.success(checked ? '已启用' : '已禁用');
            await loadConfigs();
        } catch (error: any) {
            console.error('更新状态失败:', error);
            messageApi.error('更新状态失败: ' + (error.message || '未知错误'));
        }
    };

    /**
     * 手动触发 temporary top20 归集比对
     */
    const handleSyncTemporary = async () => {
        try {
            setSyncingTemporary(true);
            const response = await exchangeDataConfigService.syncTemporaryConfigs();
            const apiResponse = response.data;
            if ('0' === apiResponse.code) {
                messageApi.success(`temporary top20 归集完成，已比对 ${apiResponse.data || 0} 个品种`);
                await loadConfigs();
            } else {
                messageApi.error(apiResponse.message || 'temporary top20 归集失败');
            }
        } catch (error: any) {
            console.error('temporary top20 归集失败:', error);
            messageApi.error('temporary top20 归集失败: ' + (error.message || '未知错误'));
        } finally {
            setSyncingTemporary(false);
        }
    };

    // ==================== 表格列定义 ====================

    /** 表格列配置 */
    const columns = [
        {
            title: '交易所',
            dataIndex: 'exchangeName',
            key: 'exchangeName',
            width: 120,
            render: (text: ExchangeName) => <Tag color="blue">{text}</Tag>,
        },
        {
            title: '交易对',
            dataIndex: 'symbol',
            key: 'symbol',
            width: 150,
        },
        {
            title: '产品类型',
            dataIndex: 'productType',
            key: 'productType',
            width: 100,
            render: (text: ProductType) => PRODUCT_TYPE_MAP[text] || text,
        },
        {
            title: '配置类型',
            dataIndex: 'configType',
            key: 'configType',
            width: 110,
            render: (text: ConfigType) => (
                'temporary' === text ? (
                    <Tooltip title="temporary 自动归集来源说明，当前按 top20 同步">
                        <Tag color="volcano">
                            {CONFIG_TYPE_MAP[text] || text}
                        </Tag>
                    </Tooltip>
                ) : (
                    <Tag color="blue">
                        {CONFIG_TYPE_MAP[text] || text}
                    </Tag>
                )
            ),
        },
        {
            title: '数据源',
            dataIndex: 'dataSource',
            key: 'dataSource',
            width: 150,
            render: (sources: DataSource[]) => (
                <Tooltip title={sources.map(s => DATA_SOURCE_MAP[s]).join(', ')}>
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis'
                    }}>
                        {sources.slice(0, 1).map(source => (
                            <Tag key={source} color={source === 'live' ? 'green' : 'orange'} style={{margin: 0}}>
                                {DATA_SOURCE_MAP[source]}
                            </Tag>
                        ))}
                        {sources.length > 1 &&
                            <span style={{color: 'var(--pt-text-secondary)', fontSize: 12}}>...</span>}
                    </span>
                </Tooltip>
            ),
        },
        {
            title: '时间帧',
            dataIndex: 'timeframes',
            key: 'timeframes',
            width: 200,
            render: (tfs: Timeframe[]) => (
                <Tooltip title={tfs.map(tf => TIMEFRAME_MAP[tf]).join(', ')}>
                    <span style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 4,
                        maxWidth: '100%',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        textOverflow: 'ellipsis'
                    }}>
                        {tfs.slice(0, 1).map(tf => (
                            <Tag key={tf} color="purple" style={{margin: 0}}>{TIMEFRAME_MAP[tf]}</Tag>
                        ))}
                        {tfs.length > 1 && <span style={{color: 'var(--pt-text-secondary)', fontSize: 12}}>...</span>}
                    </span>
                </Tooltip>
            ),
        },
        {
            title: '采集频率',
            dataIndex: 'fetchIntervalSec',
            key: 'fetchIntervalSec',
            width: 120,
            render: (sec: number) => `${sec}秒`,
        },
        {
            title: '状态',
            dataIndex: 'enabled',
            key: 'enabled',
            width: 80,
            render: (enabled: 0 | 1, record: ExchangeDataConfig) => (
                <Switch
                    checked={0 === enabled}
                    disabled={'temporary' === record.configType}
                    onChange={(checked) => handleToggleEnabled(record, checked)}
                    size="small"
                />
            ),
        },
        {
            title: '操作',
            key: 'action',
            width: 120,
            render: (_: unknown, record: ExchangeDataConfig) => (
                <Space>
                    <Tooltip title="编辑">
                        <Button
                            type="text"
                            icon={<EditOutlined/>}
                            disabled={'temporary' === record.configType}
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    <Tooltip title="删除">
                        <Button
                            type="text"
                            danger
                            icon={<DeleteOutlined/>}
                            disabled={'temporary' === record.configType}
                            onClick={() => handleDelete(record)}
                        />
                    </Tooltip>
                </Space>
            ),
        },
    ];

    // ==================== 页面渲染 ====================

    return (
        <div style={{padding: '24px'}}>
            {/* 页面标题 */}
            <div style={{marginBottom: '24px'}}>
                <div style={{fontSize: '24px', fontWeight: 600, margin: 0, display: 'flex', alignItems: 'center'}}>
                    <SettingOutlined style={{marginRight: '8px'}}/>
                    交易所数据采集配置
                </div>
                <Text type="secondary">配置交易所品种、数据源、时间帧和采集频率，系统归集项仅供查看</Text>
            </div>

            {/* 筛选和工具栏 */}
            <div style={{marginBottom: '16px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Space>
                    <Text>筛选：</Text>
                    <Select
                        placeholder="交易所"
                        allowClear
                        style={{width: 120}}
                        value={filterExchange}
                        onChange={handleFilterChange(setFilterExchange)}
                        options={EXCHANGE_OPTIONS}
                    />
                    <Select
                        placeholder="产品类型"
                        allowClear
                        style={{width: 120}}
                        value={filterProductType}
                        onChange={handleFilterChange(setFilterProductType)}
                        options={PRODUCT_TYPE_OPTIONS}
                    />
                    <Select
                        placeholder="配置类型"
                        allowClear
                        style={{width: 120}}
                        value={filterConfigType}
                        onChange={handleFilterChange(setFilterConfigType)}
                        options={CONFIG_TYPE_OPTIONS}
                    />
                    <Select
                        placeholder="状态"
                        allowClear
                        style={{width: 100}}
                        value={filterEnabled}
                        onChange={handleFilterChange(setFilterEnabled)}
                        options={[
                            {value: 0, label: '启用'},
                            {value: 1, label: '禁用'},
                        ]}
                    />
                    <Input.Search
                        placeholder="输入品种筛选"
                        allowClear
                        style={{width: 180}}
                        value={filterSymbol}
                        onChange={(e) => setFilterSymbol(e.target.value || undefined)}
                        onSearch={(value) => {
                            setFilterSymbol(value || undefined);
                            setCurrentPage(0);
                        }}
                    />
                </Space>
                <Space>
                    <Tooltip title="新增配置">
                        <Button
                            type="primary"
                            icon={<PlusOutlined/>}
                            onClick={handleAdd}
                        />
                    </Tooltip>
                    <Tooltip title="temporary top20 归集">
                        <Button
                            type="primary"
                            icon={<ReloadOutlined/>}
                            onClick={handleSyncTemporary}
                            loading={syncingTemporary}
                        />
                    </Tooltip>
                </Space>
            </div>

            {/* 配置列表 */}
            <Text type="secondary" style={{marginBottom: '8px', display: 'block'}}>
                共 {total} 条配置
            </Text>
            <Table
                columns={columns}
                dataSource={configs}
                rowKey="id"
                loading={loading}
                pagination={{
                    current: currentPage + 1,
                    total: total,
                    pageSize: pageSize,
                    showTotal: (total) => `共 ${total} 条`,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50'],
                }}
                onChange={(pag) => {
                    setCurrentPage(pag.current - 1);
                    if (pag.pageSize && pag.pageSize !== pageSize) {
                        setPageSize(pag.pageSize);
                    }
                }}
                size="middle"
                locale={{emptyText: '暂无配置，请点击右上角新增'}}
            />

            {/* 新增/编辑弹窗 */}
            <Modal
                title={isEditMode ? '编辑配置' : '新增配置'}
                open={modalVisible}
                onOk={handleConfirm}
                onCancel={() => setModalVisible(false)}
                okText="确定"
                cancelText="取消"
                width={600}
            >
                <Form form={form} layout="vertical" style={{marginTop: '20px'}}>
                    <Form.Item
                        name="configType"
                        label="配置类型"
                    >
                        <Select
                            placeholder="配置类型"
                            disabled
                            options={CONFIG_TYPE_OPTIONS}
                        />
                    </Form.Item>

                    <Form.Item
                        name="exchangeName"
                        label="交易所"
                        rules={[{required: true, message: '请选择交易所'}]}
                    >
                        <Select
                            placeholder="请选择交易所"
                            disabled={isEditMode}
                            options={EXCHANGE_OPTIONS}
                        />
                    </Form.Item>

                    <Form.Item
                        name="productType"
                        label="产品类型"
                        rules={[{required: true, message: '请选择产品类型'}]}
                    >
                        <Select
                            placeholder="请选择产品类型"
                            disabled={isEditMode}
                            options={PRODUCT_TYPE_OPTIONS}
                        />
                    </Form.Item>

                    <Form.Item
                        name="symbol"
                        label="交易对"
                        rules={[
                            {required: true, message: '请输入交易对'},
                            {pattern: /^[A-Z]+\/[A-Z]+$/, message: '格式错误，应为 BTC/USDT 格式'},
                        ]}
                    >
                        <Input placeholder="请输入交易对，如 BTC/USDT" disabled={isEditMode}/>
                    </Form.Item>

                    <Form.Item
                        name="coinSymbol"
                        label="真实交易符号"
                        tooltip="如合约符号 BTC/USDT:USDT 或 BTC-USDT-SWAP"
                    >
                        <Input placeholder="如 BTC/USDT:USDT"/>
                    </Form.Item>

                    <Form.Item
                        name="dataSource"
                        label="数据源"
                        rules={[{required: true, message: '请选择数据源'}]}
                    >
                        <Checkbox.Group options={DATA_SOURCE_OPTIONS}/>
                    </Form.Item>

                    <Form.Item
                        name="timeframes"
                        label="时间帧"
                        rules={[{required: true, message: '请选择时间帧'}]}
                    >
                        <Checkbox.Group options={TIMEFRAME_OPTIONS}/>
                    </Form.Item>

                    <Form.Item
                        name="fetchIntervalSec"
                        label="采集频率（秒）"
                        rules={[{required: true, message: '请输入采集频率'}]}
                    >
                        <InputNumber
                            min={5}
                            max={86400}
                            step={5}
                            style={{width: '100%'}}
                            placeholder="请输入采集频率，以5秒为单位"
                        />
                    </Form.Item>

                    <Form.Item name="sortOrder" label="排序">
                        <InputNumber
                            min={0}
                            style={{width: '100%'}}
                            placeholder="数字越小越靠前"
                        />
                    </Form.Item>

                    <Form.Item name="enabled" label="启用" valuePropName="checked">
                        <Switch/>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default ExchangeDataSettings;
